import { useCallback, useEffect, useState } from 'react';
import { Aviso, Carregando, Dialogo, Vazio } from './ui';
import {
  descartarDaFila, drenar, verFila, type EnvioNaFila,
} from './fila';

/**
 * O que está guardado neste aparelho e ainda não subiu.
 *
 * ── Por que ela é visível ───────────────────────────────────────────
 *
 * O §18 pede «indicador de sincronização». Um número no cabeçalho diz
 * QUANTOS; esta janela diz QUAIS — e é a diferença entre o técnico saber
 * que tem sete lançamentos por subir e saber que o sétimo é a leitura do
 * PZ-03 que ele achou que tinha mandado.
 *
 * ── As duas linhas que ela distingue ────────────────────────────────
 *
 * **Esperando rede** é normal: sobe sozinha quando houver sinal.
 *
 * **Presa** é o lançamento que o servidor RECUSOU — campo inválido,
 * permissão, conflito. Insistir nela travaria todas as que vêm depois, e
 * por isso a fila a pula e a deixa aqui, com o motivo escrito. É a única
 * que precisa de gente.
 *
 * ── Por que dá para descartar ───────────────────────────────────────
 *
 * Porque uma linha presa que não sai da fila é um aviso permanente que
 * o técnico aprende a ignorar — e um indicador que ninguém olha mais é
 * pior do que indicador nenhum. O descarte apaga o que está NESTE
 * aparelho e nunca chegou ao servidor: não há dado técnico se perdendo,
 * porque nunca houve dado técnico gravado.
 */

const quando = (ms: number) =>
  new Date(ms).toLocaleString('pt-BR',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export function FilaDeEnvio({ aoFechar }: { aoFechar(): void }) {
  const [linhas, setLinhas] = useState<EnvioNaFila[] | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [tentando, setTentando] = useState(false);

  const carregar = useCallback(async () => { setLinhas(await verFila()); }, []);
  useEffect(() => { void carregar(); }, [carregar]);

  const tentar = async () => {
    setTentando(true);
    setRecado(null);
    try {
      const r = await drenar();
      setRecado(r.enviados === 0 && r.presos === 0
        ? 'Nada subiu — a rede continua fora.'
        : `${r.enviados} enviado(s)`
          + (r.presos ? `, ${r.presos} recusado(s) pelo servidor.` : '.'));
      await carregar();
    } finally { setTentando(false); }
  };

  return (
    <Dialogo aberto titulo="Fila de envio" aoFechar={aoFechar}>
      <p className="sub">
        O que está gravado neste aparelho e ainda não chegou ao servidor.
        Cada lançamento leva uma chave: reenviar não cria cópia.
      </p>

      {recado && <Aviso tipo="info">{recado}</Aviso>}

      {/* Ler o aparelho é assíncrono (IndexedDB) e, num celular carregado,
          leva um instante. Nesse instante a janela diz que está lendo: sem
          nada entre o texto e os botões, ela pareceria vazia sem dizer «nada
          na fila» (e o 132.21 a lia assim na suíte inteira). */}
      {linhas == null ? (
        <Carregando texto="Lendo o que está guardado neste aparelho" />
      ) : linhas.length === 0 ? (
        <Vazio>Nada na fila — tudo o que foi lançado já subiu.</Vazio>
      ) : (
        <ul className="lista-fila">
          {linhas.map((l) => (
            <li key={l.id} className={l.ultimoErro ? 'presa' : undefined}>
              <div className="cresce">
                <strong>{l.rotulo}</strong>
                <small className="det">
                  {quando(l.criadaEm)}
                  {l.tentativas > 0 && ` · ${l.tentativas} tentativa(s)`}
                </small>
                {l.ultimoErro && (
                  <small className="erro-fila">{l.ultimoErro}</small>
                )}
              </div>
              {l.ultimoErro && (
                <button
                  type="button" className="secundario"
                  onClick={async () => {
                    await descartarDaFila(l.id); await carregar();
                  }}
                >
                  Descartar
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="acoes">
        <button type="button" onClick={() => void tentar()} disabled={tentando}>
          {tentando ? 'Enviando…' : 'Tentar agora'}
        </button>
        <button type="button" className="secundario" onClick={aoFechar}>
          Fechar
        </button>
      </div>
    </Dialogo>
  );
}
